import { apiFetch, type TokenStore } from './client.ts';

/**
 * GET /me/pending-actions and the two act-on-it endpoints (v7.26 §7.5).
 * Surfaces invitations + unsigned contracts addressed to the logged-in user's
 * (verified) email. Requires email_verified server-side - an unverified user is
 * redirected to /verify-email by ProtectedLayout before any of this is reached.
 */

export interface PendingInvitation {
  id: string;
  practiceId: string;
  practiceName: string;
  intendedRole: string;
  makesCaseLead?: boolean;
  caseId: string | null;
  expiresAt: number;
  /** What accepting records (ToS 4.2). The server refuses a stale version. */
  acknowledgements: { scope: 'practice' | 'case'; version: string; text: string }[];
}

export interface PendingContract {
  id: string;
  practiceName: string;
  status: string;
  sentAt: number | null;
}

export interface PendingActions {
  invitations: PendingInvitation[];
  contracts: PendingContract[];
}

export async function getPendingActions(store: TokenStore): Promise<PendingActions> {
  return apiFetch<PendingActions>('/me/pending-actions', {}, store);
}

export interface AcceptInvitationResponse {
  practiceId: string;
  role: string;
  caseId: string | null;
}

/** POST /invitations/:id/accept - accept a pending invitation in-app (no code). */
export async function acceptInvitation(
  invitationId: string,
  acknowledgementVersion: string,
  store: TokenStore,
): Promise<AcceptInvitationResponse> {
  return apiFetch<AcceptInvitationResponse>(
    `/invitations/${encodeURIComponent(invitationId)}/accept`,
    { method: 'POST', body: JSON.stringify({ acknowledgementVersion }) },
    store,
  );
}

/** POST /contracts/:id/resend-signing-link - re-email the signer's link. */
export async function resendContractSigningLink(
  contractId: string,
  store: TokenStore,
): Promise<{ sent: boolean }> {
  return apiFetch<{ sent: boolean }>(
    `/contracts/${encodeURIComponent(contractId)}/resend-signing-link`,
    { method: 'POST' },
    store,
  );
}
